/**
 * AIAvailabilityChip — a subtle, always-on trust indicator that tells
 * the user Central Rentals is still working even when the AI layer is
 * running templates instead of live model calls.
 *
 * Design intent: NEVER show a scary red error. The gateway already
 * guarantees a response every time (offline template fallback). This
 * chip lets us communicate the source of the answer without spooking
 * the user. If AI is healthy → chip renders nothing. If AI is on
 * templates → renders a tiny emerald "Template" chip.
 *
 * Uses ai_gateway health endpoint that polls at low frequency.
 */
import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function AIAvailabilityChip({ compact = false }) {
  const [state, setState] = useState({ open: false, source: "live" });

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const r = await fetch(`${API}/api/system/ai-health`, { credentials: "omit" });
        if (!r.ok || cancelled) return;
        const d = await r.json();
        // Gateway reports circuit state. `open: true` means the LLM
        // vendor is currently rate-limiting / erroring — we're on
        // templates for the next few minutes.
        setState({
          open: Boolean(d.circuit_open || d.open),
          source: d.circuit_open || d.open ? "template" : "live",
        });
      } catch (_) { /* silent */ }
    }
    poll();
    const t = setInterval(poll, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Healthy → render nothing (no noise on the UI).
  if (!state.open) return null;

  return (
    <span
      data-testid="ai-availability-chip"
      title="Central Rentals is running smart templates right now — every feature keeps working."
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: compact ? "3px 8px" : "5px 10px",
        borderRadius: "9999px",
        background: "rgba(16,185,129,0.10)",
        border: "1px solid rgba(16,185,129,0.28)",
        color: "#065f46",
        fontSize: compact ? "10px" : "11px",
        fontWeight: 700,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      <ShieldCheck className="w-3 h-3" /> Smart-template mode · every feature live
    </span>
  );
}
